/**
 * TransferControls — Export/Import entry points for the catalog transfer
 * surface (PIM Wave P5 Task 8). Mounted in `FieldLibraryPage`'s header
 * actions row, alongside "Create field".
 *
 * Export triggers `GET /admin/catalog/transfer/export`, then downloads the
 * returned bundle client-side as a `catalog-bundle-<date>.json` file (a Blob
 * + a transient `<a download>` — no server-side file-attachment endpoint
 * exists; the route returns the bundle JSON body directly). Import opens
 * `ImportWizard` (upload → dry-run diff → apply).
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useExportTransferBundle } from "@/features/admin/transfer/hooks";
import { ImportWizard } from "@/features/admin/transfer/components/ImportWizard";

function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function TransferControls(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const [importOpen, setImportOpen] = useState(false);
  const exportMutation = useExportTransferBundle();

  const handleExport = (): void => {
    exportMutation.mutate(undefined, {
      onSuccess: (bundle) => {
        downloadJson(bundle, `catalog-bundle-${todayStamp()}.json`);
        toast.success(t("pim.transfer.exportSuccess"));
      },
      onError: () => toast.error(t("error.generic")),
    });
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="md"
        data-testid="pim-transfer-export-btn"
        loading={exportMutation.isPending}
        onClick={handleExport}
        className="min-h-[44px]"
      >
        {t("pim.transfer.exportButton")}
      </Button>
      <Button
        variant="outline"
        size="md"
        data-testid="pim-transfer-import-btn"
        onClick={() => setImportOpen(true)}
        className="min-h-[44px]"
      >
        {t("pim.transfer.importButton")}
      </Button>
      <ImportWizard open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}

export default TransferControls;
